<template>
  <div
    v-if="availableCashback"
    class="cashback-switcher__container">
    <div class="cashback-switcher">
      <span class="cashback-switcher__text">
        {{ "Use cashback" }}
      </span>

      <span class="cashback-switcher__amount">
        <b>{{ maxCashbackString }}</b>
      </span>

      <ui-switcher
        v-model="cashbackApplied"
        :rounded="true"
        class="cashback-switcher__switcher" />
    </div>

    <hr class="cashback-switcher__hr">
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { formatCurrencyString } from 'Root/helpers/tools';
import UiSwitcher from 'Components/ui/Switcher/UiSwitcher.vue';
import CashbackPopup from 'Components/Popups/CashbackPopup.vue';

const MAX_CASHBACK = 0.3;

export default {
  name: 'CashbackSwitcher',

  components: {
    UiSwitcher,
    // eslint-disable-next-line vue/no-unused-components
    CashbackPopup,
  },

  props: {
    price: {
      type: Number,
      default: null,
    },

    oldPrice: {
      type: Number,
      default: null,
    },

    isPaymentMethodOpened: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'default',
    },
  },

  data() {
    return {
      cashbackApplied: false,
      cashbackSum: 0,
      cashbackRecentlyApplied: false,
    };
  },

  computed: {
    ...mapState(['activeRegion']),
    ...mapGetters(['userCashback']),

    availableCashback() {
      return this.userCashback?.[`value_${this.activeRegion.shortTitle.toLowerCase()}`];
    },

    maxCashbackValue() {
      let maxPriceCashback = this.price * MAX_CASHBACK;

      if (this.type === 'gold' || this.type === 'account') {
        maxPriceCashback /= 5;
      }

      const maxCashbackValue = this.availableCashback > maxPriceCashback
        ? maxPriceCashback
        : this.availableCashback;

      return Math.round(maxCashbackValue * 100) / 100;
    },

    maxCashbackString() {
      return formatCurrencyString((this.cashbackSum || this.maxCashbackValue), this.activeRegion.id);
    },
  },

  watch: {
    cashbackApplied: {
      handler(newValue) {
        if (newValue) {
          this.openCashbackPopup();
        }

        this.$emit('cashback-applied', {
          value: this.cashbackSum,
          isApplied: newValue,
        });
      },
    },

    cashbackSum: {
      handler(newValue) {
        this.$emit('cashback-applied', {
          value: newValue,
          isApplied: this.cashbackApplied,
        });
      },
    },

    oldPrice: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.cashbackSum = 0;
          this.cashbackApplied = false;
        }
      },
    },

    price(newValue, oldValue) {
      if (newValue !== oldValue && !this.isPaymentMethodOpened && !this.cashbackRecentlyApplied) {
        this.cashbackSum = 0;
        this.cashbackApplied = false;
      }
    },
  },

  methods: {
    openCashbackPopup() {
      this.$vuedals.openMobilePanel({
        class: 'cashback-popup',
        title: 'Use cashback to reduce the price',
        component: CashbackPopup,
        data: {
          price: this.price,
          activeRegion: this.activeRegion,
          availableCashback: this.availableCashback,
          cashbackSum: this.cashbackSum,
          maxCashbackValue: this.maxCashbackValue,
          type: this.type,
        },
        onClose: ({ cashbackSum }) => {
          this.cashbackSum = cashbackSum;
          this.cashbackApplied = !!cashbackSum;
          this.cashbackRecentlyApplied = true;
          this.$nextTick(() => {
            this.cashbackRecentlyApplied = false;
          });
        },
        onDismiss: () => {
          this.cashbackApplied = !!this.cashbackSum;
          this.cashbackRecentlyApplied = false;
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cashback-switcher {
  width: 100%;
  display: flex;
  font-size: 15px;
}

.cashback-switcher__text {
  font-weight: 400;
  margin-right: auto;
}

.cashback-switcher__amount {
  margin-right: 16px;
}

.cashback-switcher__switcher {
  --width: 36px;
  --height: 24px;
  --thumb-width: 16px;
}

.cashback-switcher__hr {
  width: 100%;
  margin: 12px 0;
  border: none;
  border-top: 1px solid $color--basic-superdark-gray;
}

.cashback-switcher__container {
  display: flex;
  flex-direction: column;
}
</style>
